import { FC } from 'react';
import { useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { Box, LoadingButton, Typography } from '@thirdspacelearning/library';

import { initiateSelfLaunchedSession } from '@api';
import { useAppDispatch } from '@store';
import { loadAppData } from '@store/initialisation';
import {
    currSessionStartingSelector,
    nextLessonSelector,
} from '@store/session';
import { IntroSessionDecoration } from '@assets/dashboard';

type Props = {
    startIntroLO?: () => void;
};

const SessionIntroLO: FC<Props> = ({ startIntroLO }) => {
    const dispatch = useAppDispatch();

    const nextLesson = useSelector(nextLessonSelector);
    const showStartSession = useSelector(currSessionStartingSelector);

    const { mutate: createSelfLaunchSession, isPending } = useMutation({
        mutationFn: () => initiateSelfLaunchedSession(nextLesson?.subject_id),
        onSuccess: () => {
            dispatch(loadAppData());
        },
        onError: (error: Error) => {
            enqueueSnackbar('Please try again or refresh the page', {
                title: error.message,
                testId: 'self-launch-error',
                variant: 'sxSnackbar',
                severity: 'error',
            });
        },
    });

    const handleIntroLO = () => {
        createSelfLaunchSession();
    };

    return (
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            px={4}
            py={2}
        >
            <Box
                display="flex"
                flexDirection="column"
                mr={1}
                flexGrow={1}
                width="100%"
                gap={2}
            >
                <Box>
                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        data-testid="welcome-to-tsl-text"
                    >
                        Welcome to Third Space Learning!
                    </Typography>
                    <Typography
                        variant="subtitle2"
                        data-testid="start-intro-lo-text"
                    >
                        Let’s begin! Start your first session and say hello to
                        Skye.
                    </Typography>
                </Box>

                {!showStartSession && (
                    <Box flexShrink={0}>
                        <LoadingButton
                            variant="contained"
                            color="primary"
                            onClick={startIntroLO || handleIntroLO}
                            loading={isPending}
                            data-testid="start-intro-lo"
                        >
                            Start your first session
                        </LoadingButton>
                    </Box>
                )}
            </Box>

            {!showStartSession && (
                <Box
                    display="flex"
                    flexShrink={0}
                    overflow="hidden"
                    height="150px"
                >
                    <IntroSessionDecoration />
                </Box>
            )}

            {showStartSession && (
                <Box flexShrink={0}>
                    <LoadingButton
                        variant="contained"
                        color="primary"
                        onClick={startIntroLO || handleIntroLO}
                        loading={isPending}
                        data-testid="start-intro-lo"
                    >
                        Start Lesson
                    </LoadingButton>
                </Box>
            )}
        </Box>
    );
};

export default SessionIntroLO;
